import axios from 'axios';

/**
 * ROAR-4970 (ROAR-4802 / P1.11, PR3) — Invite Teammate + row-action mutations.
 *
 * Unlike PR2's `getMembers` (read, NO_COMPANY_SCOPE_REQUEST_CONFIG), these ARE company-scoped by
 * the standard axios-defaults pattern (`company_id`/`selected_company_uuid` headers set at
 * bootstrap / by the Selected-Customer dropdown) — confirmed against the shipped backend: an
 * mssp_admin invite/edit/remove call is deliberately NOT exempted from requiring
 * `selected_company_uuid` (`resolveCompanyScope.js`'s own comment: "PR2's mutation routes and
 * PR3's invite routes... are deliberately NOT added [to the header-optional list] and must keep
 * denying on a missing header" — since AC15 requires a real target company for these).
 *
 * Error responses (seat cap, non-leaking conflict, last-write-admin, tier/role validation) all
 * carry a `message` in the response body — this module does no special-casing per cause; the
 * caller (the mutation's `onError`) displays `error?.response?.data?.message` verbatim, matching
 * this codebase's established convention (e.g. src/containers/pages/SignUp/OtpStep.js).
 */

/**
 * POST /team/invite (AC1, AC10, AC15).
 *
 * @param {object} payload
 * @param {string} payload.email
 * @param {string} [payload.first_name]
 * @param {string} [payload.last_name]
 * @param {string} payload.platform_role One of the pairs `getGrantableRoles` returns for the caller.
 * @param {string} payload.access_mode
 * @returns {Promise<{success: boolean}>}
 */
export const inviteMember = async (payload) => {
  const res = await axios.post('/team/invite', payload);
  return res.data;
};

/**
 * PATCH /team/members/:id (AC2, AC3).
 *
 * @param {object} params
 * @param {number} params.id The MEMBER's (user) id — never the caller's own (blocked server-side, AC3).
 * @param {string} [params.platform_role] Must stay within the target's own tier family.
 * @param {string} [params.access_mode]
 * @returns {Promise<{member: object}>}
 */
export const updateMemberRoleAccess = async ({ id, ...changes }) => {
  const res = await axios.patch(`/team/members/${id}`, changes);
  return res.data;
};

/**
 * DELETE /team/members/:id (AC2, AC3). Idempotent server-side.
 *
 * @param {number} id
 * @returns {Promise<{success: boolean}>}
 */
export const removeMember = async (id) => {
  const res = await axios.delete(`/team/members/${id}`);
  return res.data;
};

/**
 * POST /team/invite/:inviteId/resend (AC10, AC13) — already shipped under ROAR-4853; unblocked
 * for the FE by a backend follow-up exposing `invite_id` on GET /team/members's invited rows
 * (derived from actual pending-invite existence, not a `status` literal — `users.status` has no
 * `invited` value; see Members/index.js's module comment). 409s with INVITE_NOT_PENDING if the
 * invite is no longer pending (already accepted/cancelled) — displayed verbatim like every other
 * refusal here.
 *
 * @param {number} inviteId The `invites` table's own id (row.invite_id), never the user's id.
 * @returns {Promise<{success: boolean}>}
 */
export const resendInvite = async (inviteId) => {
  const res = await axios.post(`/team/invite/${inviteId}/resend`);
  return res.data;
};

/**
 * PUT /team/invite/:inviteId/cancel (AC13) — already shipped under ROAR-4853; same invite_id
 * unblock as `resendInvite`. 409s with INVITE_NOT_PENDING if already accepted/cancelled.
 *
 * Known backend gap (out of scope here, flagged by the backend author): cancelling only flips
 * `invites.status`, never `users.status` — so the row doesn't disappear from the Members list
 * per AC13's letter, it just loses its `invite_id` (and so its Cancel/Re-Invite actions) on the
 * next list refresh and reads as a plain `inactive` member instead.
 *
 * @param {number} inviteId The `invites` table's own id (row.invite_id), never the user's id.
 * @returns {Promise<{success: boolean}>}
 */
export const cancelInvite = async (inviteId) => {
  const res = await axios.put(`/team/invite/${inviteId}/cancel`);
  return res.data;
};
